import type { PriceLookupRow } from "@/lib/api/assistant";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  commodityLabel,
  formatNum,
  formatBarTs,
  formatWindow,
  instrumentLabel,
} from "../utils";

function SingleBar({ row }: { row: PriceLookupRow }) {
  const bar = row.bars[0];
  if (!bar) {
    return <div className="p-4 text-sm text-slate-500 italic">No bar in window.</div>;
  }
  return (
    <div className="p-6 flex items-baseline gap-4 flex-wrap">
      <div>
        <div className="text-[11px] uppercase tracking-wide text-slate-500">Close</div>
        <div className="text-3xl font-semibold tabular-nums text-slate-900 mt-1">
          {formatNum(bar.close)}
        </div>
      </div>
      <div className="text-sm text-slate-600">on {formatBarTs(bar.timestamp, row.bar_display)}</div>
    </div>
  );
}

function BarsTable({ row }: { row: PriceLookupRow }) {
  if (row.bars.length === 0) {
    return <div className="p-4 text-sm text-slate-500 italic">No bars in window.</div>;
  }
  return (
    <div className="overflow-x-auto max-h-96 overflow-y-auto">
      <table className="text-sm w-full border-collapse">
        <thead className="sticky top-0 bg-slate-50">
          <tr className="text-slate-600 text-left">
            <th className="px-3 py-2 font-medium">Date</th>
            <th className="px-3 py-2 font-medium text-right">Close</th>
          </tr>
        </thead>
        <tbody>
          {row.bars.map((bar, i) => (
            <tr key={i} className="border-t border-slate-200">
              <td className="px-3 py-2 whitespace-nowrap">{formatBarTs(bar.timestamp, row.bar_display)}</td>
              <td className="px-3 py-2 tabular-nums text-right">{formatNum(bar.close)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Row({ row }: { row: PriceLookupRow }) {
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
      <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <div className="text-sm font-semibold text-slate-900 flex items-center gap-2">
            {commodityLabel(row.commodity_id)}
            <Badge variant="outline" className="text-[10px]">select: {row.select}</Badge>
            <Badge variant="outline" className="text-[10px]">{row.bar_count} bars</Badge>
          </div>
          <div className="text-xs text-slate-500 mt-0.5">{instrumentLabel(row.instrument)}</div>
        </div>
        <div className="text-xs text-slate-500 text-right">
          <div>{formatWindow(row.window)}</div>
        </div>
      </div>
      {row.select === "single" ? <SingleBar row={row} /> : <BarsTable row={row} />}
    </div>
  );
}

export function PriceLookupResult({ rows }: { rows: PriceLookupRow[] }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Price Lookup</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {rows.length === 0 && (
          <div className="text-sm text-slate-500 italic">No rows returned.</div>
        )}
        {rows.map((row, i) => (
          <Row key={`${row.commodity_id}-${i}`} row={row} />
        ))}
      </CardContent>
    </Card>
  );
}
